/**
 * useAdSheetLogic - Loop-resistant Ad Sheet state management hook
 *
 * Architecture principles:
 * 1. NO objects/arrays in useEffect dependencies
 * 2. Ref-based access for async functions and event handlers
 * 3. Fingerprinting to prevent unnecessary state updates
 * 4. Stable callbacks that rarely change identity
 * 5. Debounced auto-save to Supabase
 */

import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { generateAdName as generateAdNameUtil, type GenerateAdNameParams } from "@/utils/adNaming";
import { useQuery, useQueries, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useAdAccountSettings } from "@/hooks/useAdAccountSettings";
import { useBackgroundSync } from "@/hooks/useBackgroundSync";
import type {
  AdRow,
  AdFormat,
  AdStage,
  Campaign,
  AdSet,
  FacebookPage,
  InstagramAccount,
  CreativeOption,
  CarouselCard,
  CollectionHeadlineType,
  CatalogHeadlineType,
  OptionalColumn,
  PartnerFacebookPage,
  PartnerInstagramAccount,
} from "@/types/bulkAd";
import { createEmptyRow, AD_FORMAT_MAP } from "@/types/bulkAd";
import type { Database } from "@/integrations/supabase/types";
import { URL_REQUIRED_DESTINATIONS } from "@/hooks/useAdSetFieldRules";

// Type for Supabase row
type DbAdSheetRow = Database["public"]["Tables"]["ad_sheet_rows"]["Row"];
type DbAdSheetRowInsert = Database["public"]["Tables"]["ad_sheet_rows"]["Insert"];

// Extended type to include horizontal_creative (may not be in generated types yet)
interface ExtendedDbRow extends Partial<DbAdSheetRow> {
  id: string;
  sheet_id: string;
  user_id: string;
  stage: string;
  horizontal_creative?: string | null;
  [key: string]: unknown;
}

// Dropdown options structure
export interface DropdownOptions {
  campaigns: Campaign[];
  adSets: Record<string, AdSet[]>; // keyed by campaign_id
  facebookPages: FacebookPage[];
  instagramAccounts: InstagramAccount[];
  partnerFacebookPages: PartnerFacebookPage[];
  partnerInstagramAccounts: PartnerInstagramAccount[];
  creatives: {
    square: CreativeOption[];
    vertical: CreativeOption[];
    horizontal: CreativeOption[];
  };
}

// Cache entry with TTL
interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const DEBOUNCE_DELAY = 1500; // 1.5 seconds for auto-save

/**
 * Generate a fingerprint for an array of rows to detect actual changes
 */
function generateRowsFingerprint(rows: AdRow[]): string {
  return rows
    .map(
      (r) =>
        `${r.id}|${r.adName}|${r.stage}|${r.adFormat}|${r.campaign?.id}|${r.adSet?.id}|` +
        `${r.creatives?.feed?.filename}|${r.creatives?.vertical?.filename}|${r.creatives?.horizontal?.filename}|` +
        `${r.facebookPage?.id}|${r.instagramAccount?.id}|` +
        `${r.partnerFacebookPage?.id}|${r.partnerInstagramAccount?.id}|` +
        `${r.catalogId}|${r.productSetId}|` +
        `${r.primaryTexts.join(",")}|${r.headlines.join(",")}|${r.descriptions.join(",")}|` +
        `${r.url}|${r.ctaType}|${r.status}|${r.displayLink}|${r.utmParameters}|` +
        `${JSON.stringify(r.carouselData)}|${JSON.stringify(r.collectionData)}|` +
        `${JSON.stringify(r.catalogAdData)}|${JSON.stringify(r.flexibleData)}|` +
        `${r.ad_schedule_start}|${r.ad_schedule_end}|${r.ad_template_id}|${r.leadgenFormId}|` +
        `${(r.productTags || []).filter((t) => t && t.trim()).join(",")}|${r.ad_name_locked}`,
    )
    .join("||");
}

/**
 * Generate a fingerprint for dropdown options
 */
function generateDropdownFingerprint(options: DropdownOptions): string {
  const campaignIds = options.campaigns.map((c) => c.id).join(",");
  const adSetKeys = Object.keys(options.adSets).sort().join(",");
  const pageIds = options.facebookPages.map((p) => p.id).join(",");
  const igIds = options.instagramAccounts.map((ig) => ig.id).join(",");
  const squareCount = options.creatives.square.length;
  const verticalCount = options.creatives.vertical.length;
  const horizontalCount = options.creatives.horizontal.length;

  return `${campaignIds}|${adSetKeys}|${pageIds}|${igIds}|${squareCount}|${verticalCount}|${horizontalCount}`;
}

/**
 * Map database row to AdRow type
 * Note: Thumbnails require async lookup - use enrichRowsWithThumbnails after mapping
 */
function mapDbRowToAdRow(r: ExtendedDbRow, adAccountId: string): AdRow {
  // For Single Image/Video formats, catalog data is stored directly on the row
  const isSingle = r.ad_format === "Single Image or Video" || r.ad_format === null;

  return {
    id: r.id,
    adName: r.ad_name || "",
    stage: (r.stage as AdStage) || "Draft",
    adFormat: (r.ad_format as AdFormat) || null,
    adAccount: { id: adAccountId, name: adAccountId },
    campaign: r.campaign_id && r.campaign_name ? { id: r.campaign_id, name: r.campaign_name } : null,
    adSet: r.adset_id && r.adset_name ? { id: r.adset_id, name: r.adset_name } : null,
    // Creatives: filename is stored, thumbnail/id are placeholders to be enriched
    creatives: {
      feed: r.square_creative
        ? {
            id: "",
            filename: r.square_creative,
            thumbnail: null,
            meta_creative_id: r.meta_creative_id || "",
          }
        : null,
      vertical: r.vertical_creative
        ? {
            id: "",
            filename: r.vertical_creative,
            thumbnail: null,
            meta_creative_id: "",
          }
        : null,
      horizontal: r.horizontal_creative
        ? {
            id: "",
            filename: r.horizontal_creative,
            thumbnail: null,
            meta_creative_id: "",
          }
        : null,
    },
    carouselData:
      r.carousel_data && r.ad_format === "Carousel" ? (r.carousel_data as unknown as { cards: CarouselCard[] }) : null,
    collectionData:
      r.ad_format === "Collection"
        ? {
            creativeName: r.square_creative || "",
            catalogId: null,
            catalogName: null,
            productSetId: null,
            productSetName: null,
            headlineType: (r.collection_headline_type as CollectionHeadlineType) || "macro",
            headlineValue: r.collection_headline_value || "{{product.name}}",
            buttonLabel: (r as any).catalog_cta_type || "Shop Now",
            destinationUrl: "",
            itemHeadline: r.collection_headline_value || "{{product.name}}",
            itemDescription: (r as any).description_1 || "{{product.current_price}}",
            descriptionType:
              (r as any).description_1 && !(r as any).description_1.startsWith("{{")
                ? ("custom" as const)
                : ("macro" as const),
          }
        : null,
    catalogAdData:
      r.ad_format === "Catalog"
        ? {
            catalogId: r.catalog_id,
            catalogName: r.catalog_name || null,
            productSetId: r.product_set_id || null,
            productSetName: r.product_set_name || null,
            headlineType:
              (r.collection_headline_type as CatalogHeadlineType) ||
              (r.headline_1 && /\{\{.+\}\}/.test(r.headline_1) ? "macro" : r.headline_1 ? "custom" : "macro"),
            headlineValue: r.collection_headline_value || r.headline_1 || "{{product.name}}",
            formatType: ((r as Record<string, unknown>).catalog_format_type as "carousel" | "single") || "carousel",
            primaryText: ((r as Record<string, unknown>).catalog_primary_text as string) || "",
            destinationUrl: ((r as Record<string, unknown>).catalog_destination_url as string) || "",
            ctaType: ((r as Record<string, unknown>).catalog_cta_type as string) || "SHOP_NOW",
            descriptionType: (() => {
              const descVal =
                ((r as Record<string, unknown>).catalog_description_field as string) || r.description_1 || "";
              return descVal && !/\{\{.+\}\}/.test(descVal) ? ("custom" as const) : ("macro" as const);
            })(),
            descriptionValue:
              ((r as Record<string, unknown>).catalog_description_field as string) || r.description_1 || "",
            automatedProductTags: ((r as Record<string, unknown>).catalog_automated_product_tags as boolean) ?? false,
          }
        : null,
    flexibleData:
      r.ad_format === "Flexible" && r.flexible_data
        ? (r.flexible_data as unknown as import("@/types/bulkAd").FlexibleData)
        : null,
    // Direct catalog fields - always populate from DB for ProductSetCell display
    catalogId: r.catalog_id || null,
    catalogName: r.catalog_name || null,
    productSetId: r.product_set_id || null,
    productSetName: r.product_set_name || null,
    facebookPage:
      r.facebook_page_id && r.facebook_page_name ? { id: r.facebook_page_id, name: r.facebook_page_name } : null,
    instagramAccount:
      r.instagram_actor_id && r.instagram_name ? { id: r.instagram_actor_id, username: r.instagram_name } : null,
    partnerFacebookPage:
      r.partner_fb_page_id && r.partner_fb_page_name
        ? { id: r.partner_fb_page_id, username: r.partner_fb_page_name }
        : null,
    partnerInstagramAccount:
      r.partner_instagram_id && r.partner_instagram_name
        ? { id: r.partner_instagram_id, username: r.partner_instagram_name }
        : null,
    primaryTexts: [
      r.primary_text_1 || "",
      r.primary_text_2 || "",
      r.primary_text_3 || "",
      r.primary_text_4 || "",
      r.primary_text_5 || "",
    ],
    headlines: [r.headline_1 || "", r.headline_2 || "", r.headline_3 || "", r.headline_4 || "", r.headline_5 || ""],
    descriptions: [
      r.description_1 || "",
      r.description_2 || "",
      r.description_3 || "",
      r.description_4 || "",
      r.description_5 || "",
    ],
    productTags: Array.isArray(r.product_tags)
      ? [...r.product_tags, "", "", "", "", ""].slice(0, 5)
      : ["", "", "", "", ""],
    url: r.destination_url || "",
    displayLink: r.display_link || "",
    utmParameters: r.utm_parameters || "",
    ctaType: r.cta || "SHOP_NOW",
    status: (r.status as "PAUSED" | "ACTIVE") || "PAUSED",
    threadsPageId: r.threads_page_id || "",
    ad_template_id: r.ad_template_id || null,
    ad_name_locked: r.ad_name_locked !== false, // default to true
    ad_schedule_start: r.ad_schedule_start || null,
    ad_schedule_end: r.ad_schedule_end || null,
    leadgenFormId: r.leadgen_form_id || null,
  };
}

/**
 * Enrich rows with thumbnail URLs from creative_metadata
 * This performs a batch lookup to avoid N+1 queries
 */
async function enrichRowsWithThumbnails(rows: AdRow[], userId: string, adAccountId: string): Promise<AdRow[]> {
  // Collect all unique filenames that need thumbnail lookup
  const filenames = new Set<string>();
  for (const row of rows) {
    if (row.creatives?.feed?.filename) filenames.add(row.creatives.feed.filename);
    if (row.creatives?.vertical?.filename) filenames.add(row.creatives.vertical.filename);
    if (row.creatives?.horizontal?.filename) filenames.add(row.creatives.horizontal.filename);
  }

  if (filenames.size === 0) return rows;

  // Batch fetch all creatives by filename
  const { data: creatives, error } = await supabase
    .from("creative_metadata")
    .select("id, filename, thumbnail_url, meta_creative_id")
    .eq("user_id", userId)
    .eq("meta_account_id", adAccountId)
    .in("filename", Array.from(filenames));

  if (error) {
    console.error("Error fetching thumbnails:", error);
    return rows;
  }

  // Build lookup map: filename -> { id, thumbnail, meta_creative_id }
  const thumbnailMap = new Map<string, { id: string; thumbnail: string | null; meta_creative_id: string }>();
  for (const c of creatives || []) {
    thumbnailMap.set(c.filename, {
      id: c.id,
      thumbnail: c.thumbnail_url,
      meta_creative_id: c.meta_creative_id,
    });
  }

  // Enrich each row with thumbnail data
  return rows.map((row) => {
    if (!row.creatives) return row;

    const enrichCreative = (creative: typeof row.creatives.feed) => {
      if (!creative?.filename) return creative;
      const meta = thumbnailMap.get(creative.filename);
      if (!meta) return creative;
      return {
        ...creative,
        id: meta.id,
        thumbnail: meta.thumbnail,
        meta_creative_id: meta.meta_creative_id,
      };
    };

    return {
      ...row,
      creatives: {
        feed: enrichCreative(row.creatives.feed),
        vertical: enrichCreative(row.creatives.vertical),
        horizontal: enrichCreative(row.creatives.horizontal),
      },
    };
  });
}

/**
 * Map AdRow to database insert format
 */
function mapAdRowToDbInsert(row: AdRow, sheetId: string, userId: string, index: number): DbAdSheetRowInsert {
  const isCarousel = row.adFormat === "Carousel";
  const isCollection = row.adFormat === "Collection";
  const isCatalog = row.adFormat === "Catalog";
  const isFlexible = row.adFormat === "Flexible";
  const isSingle = row.adFormat === "Single Image or Video" || row.adFormat === null;

  // Determine what to store in carousel_data (JSONB column for Carousel only)
  let carouselDataValue = null;
  if (isCarousel && row.carouselData) {
    carouselDataValue = JSON.parse(JSON.stringify(row.carouselData));
  }

  // Flexible data goes into its own flexible_data column
  let flexibleDataValue = null;
  if (isFlexible && row.flexibleData) {
    flexibleDataValue = JSON.parse(JSON.stringify(row.flexibleData));
  }

  // Catalog/product set: for Catalog format, source from catalogAdData; otherwise from row-level fields
  const catalogId = isCatalog ? row.catalogAdData?.catalogId || null : row.catalogId || null;
  const catalogName = isCatalog ? row.catalogAdData?.catalogName || null : row.catalogName || null;
  const productSetId = isCatalog ? row.catalogAdData?.productSetId || null : row.productSetId || null;
  const productSetName = isCatalog ? row.catalogAdData?.productSetName || null : row.productSetName || null;

  return {
    id: row.id, // Preserve existing ID for upsert
    sheet_id: sheetId,
    user_id: userId,
    row_order: index,
    ad_name: row.adName || null,
    stage: row.stage || "Draft",
    ad_format: row.adFormat || null,
    campaign_id: row.campaign?.id || null,
    campaign_name: row.campaign?.name || null,
    adset_id: row.adSet?.id || null,
    adset_name: row.adSet?.name || null,
    square_creative: isCollection
      ? row.collectionData?.creativeName || null
      : isCarousel || isCatalog || isFlexible
        ? null
        : row.creatives?.feed?.filename || null,
    vertical_creative:
      isCarousel || isCollection || isCatalog || isFlexible ? null : row.creatives?.vertical?.filename || null,
    // horizontal_creative handled separately if column exists
    carousel_data: carouselDataValue,
    flexible_data: flexibleDataValue,
    catalog_id: catalogId,
    catalog_name: catalogName,
    product_set_id: productSetId,
    product_set_name: productSetName,
    collection_headline_type: isCollection
      ? row.collectionData?.headlineType || "custom"
      : isCatalog
        ? (row.headlines[0] || "").includes("{{")
          ? "macro"
          : "custom"
        : null,
    collection_headline_value: isCollection
      ? row.collectionData?.headlineValue || null
      : isCatalog
        ? row.headlines[0] || null
        : null,
    catalog_primary_text: isCatalog ? row.primaryTexts[0] || row.catalogAdData?.primaryText || null : null,
    catalog_destination_url: isCatalog ? row.url || row.catalogAdData?.destinationUrl || null : null,
    catalog_cta_type: isCollection
      ? row.collectionData?.buttonLabel || "Shop Now"
      : isCatalog
        ? row.ctaType || row.catalogAdData?.ctaType || "SHOP_NOW"
        : null,
    catalog_format_type: isCatalog ? row.catalogAdData?.formatType || "carousel" : null,
    catalog_description_field: isCatalog ? row.descriptions?.[0] || row.catalogAdData?.descriptionValue || null : null,
    catalog_automated_product_tags: isCatalog ? (row.catalogAdData?.automatedProductTags ?? false) : false,
    facebook_page_id: row.facebookPage?.id || null,
    facebook_page_name: row.facebookPage?.name || null,
    instagram_actor_id: row.instagramAccount?.id || null,
    instagram_name: row.instagramAccount?.username || null,
    partner_fb_page_id: row.partnerFacebookPage?.id || null,
    partner_fb_page_name: row.partnerFacebookPage?.username || null,
    partner_instagram_id: row.partnerInstagramAccount?.id || null,
    partner_instagram_name: row.partnerInstagramAccount?.username || null,
    primary_text_1: row.primaryTexts[0] || null,
    primary_text_2: isCarousel || isCollection || isCatalog ? null : row.primaryTexts[1] || null,
    primary_text_3: isCarousel || isCollection || isCatalog ? null : row.primaryTexts[2] || null,
    primary_text_4: isCarousel || isCollection || isCatalog ? null : row.primaryTexts[3] || null,
    primary_text_5: isCarousel || isCollection || isCatalog ? null : row.primaryTexts[4] || null,
    headline_1: isCarousel ? null : row.headlines[0] || null,
    headline_2: isCarousel || isCollection || isCatalog ? null : row.headlines[1] || null,
    headline_3: isCarousel || isCollection || isCatalog ? null : row.headlines[2] || null,
    headline_4: isCarousel || isCollection || isCatalog ? null : row.headlines[3] || null,
    headline_5: isCarousel || isCollection || isCatalog ? null : row.headlines[4] || null,
    description_1: isCollection ? row.collectionData?.itemDescription || null : row.descriptions?.[0] || null,
    description_2: isFlexible ? row.descriptions?.[1] || null : null,
    description_3: isFlexible ? row.descriptions?.[2] || null : null,
    description_4: isFlexible ? row.descriptions?.[3] || null : null,
    description_5: isFlexible ? row.descriptions?.[4] || null : null,
    destination_url: row.url || null,
    display_link: isCollection ? null : row.displayLink || null,
    utm_parameters: row.utmParameters || null,
    cta: row.ctaType || "SHOP_NOW",
    status: row.status || "PAUSED",
    threads_page_id: row.threadsPageId || null,
    product_tags: isCollection || isCatalog ? null : row.productTags?.filter((t) => t.trim()) || null,
    ad_template_id: row.ad_template_id || null,
    ad_name_locked: row.ad_name_locked !== false,
    ad_schedule_start: row.ad_schedule_start || null,
    ad_schedule_end: row.ad_schedule_end || null,
    leadgen_form_id: row.leadgenFormId || null,
  };
}

export interface UseAdSheetLogicReturn {
  rows: AdRow[];
  isLoading: boolean;
  isSaving: boolean;
  saveError: string | null;
  updateRow: (rowId: string, field: keyof AdRow | string, value: unknown) => void;
  updateRowBatch: (rowId: string, updates: Partial<AdRow>) => void;
  addRow: (afterRowId?: string) => void;

  addMultipleRows: (
    rows: Array<{
      ad_format: string;
      ad_name: string;
      creative_feed?: string | null;
      meta_creative_id_feed?: string | null;
      creative_vertical?: string | null;
      meta_creative_id_vertical?: string | null;
      creative_horizontal?: string | null;
      meta_creative_id_horizontal?: string | null;
      thumbnail_url?: string | null;
      carousel_data?: unknown;
      flexible_data?: unknown;
      // Collection fields
      collection_headline_type?: string | null;
      collection_headline_value?: string | null;
      catalog_cta_type?: string | null;
      description_1?: string | null;
    }>,
  ) => Promise<void>;
  deleteRows: (rowIds: string[]) => void;
  duplicateRow: (rowId: string) => void;
  reorderRows: (startIndex: number, endIndex: number) => void;
  dropdownOptions: DropdownOptions;
  getAdSetsForCampaign: (campaignId: string) => AdSet[];
  getAdSetWithDetails: (campaignId: string, adSetId: string) => AdSet | null;
  refreshMetaSync: () => Promise<void>;
  forceSave: () => Promise<void>;
  sheetInfo: { adAccountId: string; name: string } | null;
  processingRowIds: Set<string>;
  launchProgress: { current: number; total: number } | null;
  launchAds: (rowIds: string[]) => Promise<LaunchResult>;
  // Column visibility
  visibleOptionalColumns: OptionalColumn[];
  toggleOptionalColumn: (column: OptionalColumn) => void;
  // Row selection
  selectedRowIds: Set<string>;
  toggleRowSelection: (rowId: string) => void;
  selectAllRows: () => void;
  clearSelection: () => void;
  allRowsSelected: boolean;
  someRowsSelected: boolean;
  // Sheet-level catalog
  catalogId: string | null;
  setCatalogId: (catalogId: string | null) => void;
  namingConvention: GenerateAdNameParams["namingConvention"];
  generateRowName: (row: AdRow) => string;
}

export interface LaunchResult {
  success: boolean;
  launched: number;
  failed: number;
  errors: Array<{ rowId: string; adName: string; error: string }>;
}

export const useAdSheetLogic = (sheetId: string): UseAdSheetLogicReturn => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // ===== STATE =====
  const [rows, setRows] = useState<AdRow[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [dropdownOptions, setDropdownOptions] = useState<DropdownOptions>({
    campaigns: [],
    adSets: {},
    facebookPages: [],
    instagramAccounts: [],
    partnerFacebookPages: [],
    partnerInstagramAccounts: [],
    creatives: { square: [], vertical: [], horizontal: [] },
  });
  // Launch state
  const [processingRowIds, setProcessingRowIds] = useState<Set<string>>(new Set());
  const [launchProgress, setLaunchProgress] = useState<{ current: number; total: number } | null>(null);
  // Column visibility state
  const [visibleOptionalColumns, setVisibleOptionalColumns] = useState<OptionalColumn[]>([]);
  // Row selection state
  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set());
  // Sheet-level catalog state
  const [catalogId, setCatalogIdState] = useState<string | null>(null);
  // ===== REFS for stable access in async functions =====
  const rowsRef = useRef<AdRow[]>(rows);
  const rowsFingerprintRef = useRef<string>("");
  const dropdownOptionsRef = useRef<DropdownOptions>(dropdownOptions);
  const dropdownFingerprintRef = useRef<string>("");
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSaveRef = useRef<boolean>(false);
  const adAccountIdRef = useRef<string>("");
  const accountSettingsRef = useRef<any>(null);

  // Keep refs in sync with state (but don't trigger effects!)
  rowsRef.current = rows;
  dropdownOptionsRef.current = dropdownOptions;

  // ===== FETCH SHEET & ROWS with React Query =====
  const { data: sheetData, isLoading: isLoadingSheet } = useQuery({
    queryKey: ["ad-sheet", sheetId],
    queryFn: async () => {
      const { data, error } = await supabase.from("ad_sheets").select("*").eq("id", sheetId).single();

      if (error) throw error;
      return data;
    },
    enabled: !!sheetId && !!user?.id,
    staleTime: Infinity, // Sheet metadata rarely changes
  });

  const { data: rowsData, isLoading: isLoadingRows } = useQuery({
    queryKey: ["ad-sheet-rows", sheetId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ad_sheet_rows")
        .select("*")
        .eq("sheet_id", sheetId)
        .order("row_order", { ascending: true });

      if (error) throw error;
      return data as ExtendedDbRow[];
    },
    enabled: !!sheetId && !!user?.id,
    staleTime: 0, // Always fetch fresh on mount
  });

  // Store adAccountId in ref when sheet data loads
  useEffect(() => {
    if (sheetData?.ad_account_id) {
      adAccountIdRef.current = sheetData.ad_account_id;
    }
  }, [sheetData?.ad_account_id]);

  // Fetch ad account settings for defaults on new rows

  const { data: accountSettings } = useAdAccountSettings(user?.id || "", sheetData?.ad_account_id || "");
  const { triggerBackgroundSync } = useBackgroundSync();
  const hasSyncedOnMountRef = useRef(false);
  const lastSyncedAccountRef = useRef<string | null>(null);

  useEffect(() => {
    accountSettingsRef.current = accountSettings;
  }, [accountSettings]);

  // Initialize visible optional columns from sheet data
  useEffect(() => {
    if (sheetData?.visible_columns && Array.isArray(sheetData.visible_columns)) {
      setVisibleOptionalColumns(sheetData.visible_columns as OptionalColumn[]);
    }
  }, [sheetData?.visible_columns]);

  // Initialize catalog_id from sheet data
  useEffect(() => {
    if (sheetData) {
      setCatalogIdState((sheetData as any).catalog_id || null);
    }
  }, [sheetData]);

  // ===== FETCH CAMPAIGNS from cache =====
  const { data: campaignsData } = useQuery({
    queryKey: ["cached-campaigns", sheetData?.ad_account_id],
    queryFn: async () => {
      if (!sheetData?.ad_account_id) return [];

      const { data, error } = await supabase
        .from("meta_cached_campaigns")
        .select("campaign_id, campaign_name, display_name, objective")
        .eq("ad_account_id", sheetData.ad_account_id)
        .order("campaign_name", { ascending: true });

      if (error) {
        console.error("Error fetching cached campaigns:", error);
        return [];
      }

      return (data || []).map((c) => ({
        id: c.campaign_id,
        name: c.display_name || c.campaign_name,
        objective: c.objective || null,
      }));
    },
    enabled: !!sheetData?.ad_account_id,
    staleTime: 0,
  });

  useEffect(() => {
    if (!user?.id || !sheetData?.ad_account_id) return;
    // Reset sync guard and invalidate stale caches when ad account changes
    if (lastSyncedAccountRef.current !== sheetData.ad_account_id) {
      hasSyncedOnMountRef.current = false;
      lastSyncedAccountRef.current = sheetData.ad_account_id;
      queryClient.invalidateQueries({ queryKey: ["cached-campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["cached-ad-sets"] });
      queryClient.invalidateQueries({ queryKey: ["ad-sets", sheetData.ad_account_id] });
    }
    if (hasSyncedOnMountRef.current) return;
    if (campaignsData && campaignsData.length > 0) return; // cache already warm
    hasSyncedOnMountRef.current = true;
    triggerBackgroundSync(user.id, sheetData.ad_account_id);
  }, [user?.id, sheetData?.ad_account_id, campaignsData, triggerBackgroundSync, queryClient]);

  // ===== FETCH FACEBOOK PAGES from cache =====
  const { data: facebookPagesData } = useQuery({
    queryKey: ["cached-facebook-pages", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from("meta_cached_facebook_pages")
        .select("page_id, page_name")
        .eq("user_id", user.id)
        .order("page_name", { ascending: true });

      if (error) {
        console.error("Error fetching cached Facebook pages:", error);
        return [];
      }

      const seen = new Set<string>();
      return (data || []).reduce((acc: FacebookPage[], p) => {
        if (!seen.has(p.page_id)) {
          seen.add(p.page_id);
          acc.push({ id: p.page_id, name: p.page_name });
        }
        return acc;
      }, []);
    },
    enabled: !!user?.id,
    staleTime: CACHE_TTL,
    refetchInterval: (query) => {
      const currentData = query.state.data;
      if (!currentData || currentData.length === 0) return 3000;
      return false;
    },
  });

  // ===== FETCH INSTAGRAM ACCOUNTS from cache =====
  const { data: instagramAccountsData } = useQuery({
    queryKey: ["cached-instagram-accounts", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from("meta_cached_instagram_accounts")
        .select("instagram_id, username")
        .eq("user_id", user.id)
        .order("username", { ascending: true });

      if (error) {
        console.error("Error fetching cached Instagram accounts:", error);
        return [];
      }

      const seen = new Set<string>();
      return (data || []).reduce((acc: InstagramAccount[], ig) => {
        if (!seen.has(ig.instagram_id)) {
          seen.add(ig.instagram_id);
          acc.push({ id: ig.instagram_id, username: ig.username });
        }
        return acc;
      }, []);
    },
    enabled: !!user?.id,
    staleTime: CACHE_TTL,
    refetchInterval: (query) => {
      const currentData = query.state.data;
      if (!currentData || currentData.length === 0) return 3000;
      return false;
    },
  });

  // ===== FETCH PARTNER FACEBOOK PAGES from cache =====
  const { data: partnerFacebookPagesData } = useQuery({
    queryKey: ["cached-partnership-facebook-pages", user?.id, sheetData?.ad_account_id],
    queryFn: async () => {
      if (!user?.id || !sheetData?.ad_account_id) return [];

      const { data, error } = await supabase
        .from("meta_cached_partnership_facebook_pages")
        .select("page_id, page_name, display_name")
        .eq("user_id", user.id)
        .eq("ad_account_id", sheetData.ad_account_id)
        .order("page_name", { ascending: true });

      if (error) {
        console.error("Error fetching cached Partner FB pages:", error);
        return [];
      }

      return (data || []).map((p) => ({
        id: p.page_id,
        name: p.page_name,
        username: p.display_name || p.page_name,
      }));
    },
    enabled: !!user?.id && !!sheetData?.ad_account_id,
    staleTime: CACHE_TTL,
  });

  // ===== FETCH PARTNER INSTAGRAM ACCOUNTS from cache =====
  const { data: partnerInstagramAccountsData } = useQuery({
    queryKey: ["cached-partnership-instagram", user?.id, sheetData?.ad_account_id],
    queryFn: async () => {
      if (!user?.id || !sheetData?.ad_account_id) return [];

      const { data, error } = await supabase
        .from("meta_cached_partnership_instagram")
        .select(
          "instagram_id, username, display_name, creator_fb_page_id, source_instagram_id, source_instagram_username",
        )
        .eq("user_id", user.id)
        .eq("ad_account_id", sheetData.ad_account_id)
        .order("username", { ascending: true });

      if (error) {
        console.error("Error fetching cached Partner IG accounts:", error);
        return [];
      }

      return (data || []).map((ig) => ({
        id: ig.instagram_id,
        username: ig.username,
        displayName: ig.display_name || ig.username,
        sourceInstagramId: ig.source_instagram_id || "",
        sourceInstagramUsername: ig.source_instagram_username || "",
        creatorFbPageId: ig.creator_fb_page_id,
      }));
    },
    enabled: !!user?.id && !!sheetData?.ad_account_id,
    staleTime: CACHE_TTL,
  });

  // ===== PARALLEL AD SETS FETCHING with useQueries =====
  // Extract unique campaign IDs from local rows state (not rowsData) to react to user edits
  const uniqueCampaignIds = useMemo(() => {
    if (!rows || rows.length === 0) return [];
    const ids = new Set<string>();
    for (const row of rows) {
      // Use row.campaign?.id since rows is the mapped AdRow[] state
      if (row.campaign?.id) {
        const cleanId = row.campaign.id.replace(/[^a-zA-Z0-9_]+$/g, "").trim();
        if (cleanId) ids.add(cleanId);
      }
    }
    return Array.from(ids);
  }, [rows]);

  // Parallel fetch ad sets for all campaigns using useQueries
  const adSetQueries = useQueries({
    queries: uniqueCampaignIds.map((campaignId) => ({
      queryKey: ["ad-sets", sheetData?.ad_account_id, campaignId],
      queryFn: async (): Promise<{ campaignId: string; adSets: AdSet[] }> => {
        const adAccountId = sheetData?.ad_account_id;
        if (!user?.id || !adAccountId) {
          return { campaignId, adSets: [] };
        }

        // STEP 1: Try DB cache first
        console.log(`[fetchAdSetsForCampaign] Cache query params: userId=${user.id}, adAccountId=${adAccountId}, campaignId=${campaignId}`);
        const { data: cachedData, error: cacheError } = await supabase
          .from("meta_cached_ad_sets")
          .select(
            "ad_set_id, ad_set_name, display_name, status, optimization_goal, promoted_object, destination_type, billing_event",
          )
          .eq("user_id", user.id)
          .eq("ad_account_id", adAccountId)
          .eq("campaign_id", campaignId)
          .in("status", ["ACTIVE", "PAUSED"])
          .order("ad_set_name");

        console.log(`[fetchAdSetsForCampaign] Cache returned ${cachedData?.length ?? 0} ad sets`);
        if (cachedData && cachedData.length > 0) {
          const adSets: AdSet[] = cachedData.map((a) => ({
            id: a.ad_set_id,
            name: a.display_name || a.ad_set_name || "",
            optimizationGoal: a.optimization_goal || null,
            promotedObject: a.promoted_object as { page_id?: string; pixel_id?: string; [key: string]: unknown } | null,
            destinationType: a.destination_type || null,
            billingEvent: a.billing_event || null,
          }));
          return { campaignId, adSets };
        }

        // STEP 2: Fallback to Edge Function
        const { data: edgeFunctionData, error: edgeFunctionError } = await supabase.functions.invoke(
          "process-integration",
          {
            body: {
              action: "get-adsets",
              user_id: user.id,
              campaign_id: campaignId,
              ad_account_id: adAccountId,
              filtering: [{ field: "effective_status", operator: "IN", value: ["ACTIVE", "PAUSED"] }],
            },
          },
        );

        if (edgeFunctionError) {
          throw edgeFunctionError;
        }

        console.log(`[fetchAdSetsForCampaign] Edge function fallback returned: ${JSON.stringify(edgeFunctionData)?.substring(0, 200)}`);
        const adSetList = edgeFunctionData
          ? (Array.isArray(edgeFunctionData) ? edgeFunctionData : edgeFunctionData.data || [])
          : [];
        const adSets: AdSet[] = adSetList
          .map(
            (a: {
              adset_id?: string;
              id?: string;
              ad_set_id?: string;
              adset_name?: string;
              ad_set_name?: string;
              name?: string;
              display_name?: string;
              optimization_goal?: string;
              promoted_object?: { page_id?: string; pixel_id?: string; [key: string]: unknown };
              destination_type?: string;
              billing_event?: string;
            }) => ({
              id: a.adset_id || a.ad_set_id || a.id || "",
              name: a.display_name || a.adset_name || a.ad_set_name || a.name || "",
              optimizationGoal: a.optimization_goal || null,
              promotedObject: a.promoted_object || null,
              destinationType: a.destination_type || null,
              billingEvent: a.billing_event || null,
            }),
          )
          .filter((a: AdSet) => a.id !== "");

        if (adSets.length > 0) {
          return { campaignId, adSets };
        }

        // n8n writes to DB cache during the edge function call but may return empty response.
        // Re-query DB cache to pick up data n8n wrote.
        const { data: refreshedCache } = await supabase
          .from("meta_cached_ad_sets")
          .select(
            "ad_set_id, ad_set_name, display_name, status, optimization_goal, promoted_object, destination_type, billing_event",
          )
          .eq("user_id", user.id)
          .eq("ad_account_id", adAccountId)
          .eq("campaign_id", campaignId)
          .in("status", ["ACTIVE", "PAUSED"])
          .order("ad_set_name");

        console.log(`[fetchAdSetsForCampaign] Re-query after fallback returned ${refreshedCache?.length ?? 0} ad sets`);
        if (refreshedCache && refreshedCache.length > 0) {
          const refreshedAdSets: AdSet[] = refreshedCache.map((a) => ({
            id: a.ad_set_id,
            name: a.display_name || a.ad_set_name || "",
            optimizationGoal: a.optimization_goal || null,
            promotedObject: a.promoted_object as { page_id?: string; pixel_id?: string; [key: string]: unknown } | null,
            destinationType: a.destination_type || null,
            billingEvent: a.billing_event || null,
          }));
          return { campaignId, adSets: refreshedAdSets };
        }

        return { campaignId, adSets: [] };
      },
      enabled: !!user?.id && !!sheetData?.ad_account_id && !!campaignId,
      staleTime: CACHE_TTL,
      retry: 1,
    })),
  });

  // Derive availableAdSets lookup from query results — no useMemo to avoid stale closures
  const availableAdSets: Record<string, AdSet[]> = {};
  for (const query of adSetQueries) {
    if (query.data) {
      availableAdSets[query.data.campaignId] = query.data.adSets;
    }
  }

  // Update dropdownOptions when campaign/page/IG/adSets/partner data changes
  useEffect(() => {
    const newOptions: DropdownOptions = {
      campaigns: campaignsData || [],
      adSets: availableAdSets,
      facebookPages: facebookPagesData || [],
      instagramAccounts: instagramAccountsData || [],
      partnerFacebookPages: partnerFacebookPagesData || [],
      partnerInstagramAccounts: partnerInstagramAccountsData || [],
      creatives: dropdownOptionsRef.current.creatives, // Preserve existing creatives
    };

    const newFingerprint = generateDropdownFingerprint(newOptions);
    if (newFingerprint !== dropdownFingerprintRef.current) {
      dropdownFingerprintRef.current = newFingerprint;
      setDropdownOptions(newOptions);
    }
  }, [
    campaignsData,
    facebookPagesData,
    instagramAccountsData,
    availableAdSets,
    partnerFacebookPagesData,
    partnerInstagramAccountsData,
  ]);

  // Map database rows to AdRow state and enrich with thumbnails
  useEffect(() => {
    if (!rowsData || !sheetData?.ad_account_id || !user?.id) return;

    const adAccountId = sheetData.ad_account_id;
    const userId = user.id;

    // Map rows first
    const mappedRows = rowsData.length > 0 ? rowsData.map((r) => mapDbRowToAdRow(r, adAccountId)) : [];

    // Enrich with thumbnails (async)
    enrichRowsWithThumbnails(mappedRows, userId, adAccountId).then((enrichedRows) => {
      // Fingerprint check to prevent unnecessary updates
      const newFingerprint = generateRowsFingerprint(enrichedRows);
      if (newFingerprint !== rowsFingerprintRef.current) {
        rowsFingerprintRef.current = newFingerprint;
        setRows(enrichedRows);
      }
    });
  }, [rowsData, sheetData?.ad_account_id, user?.id]);

  // ===== DEBOUNCED SAVE LOGIC =====
  const saveToDatabase = useCallback(async () => {
    if (!sheetId || !user?.id) return;

    const currentRows = rowsRef.current;

    setIsSaving(true);
    setSaveError(null);
    pendingSaveRef.current = false;

    try {
      const existingIds = currentRows.map((r) => r.id);

      // Delete removed rows — branch to avoid invalid SQL with empty NOT IN ()
      if (existingIds.length > 0) {
        const { error: deleteError } = await supabase
          .from("ad_sheet_rows")
          .delete()
          .eq("sheet_id", sheetId)
          .not("id", "in", `(${existingIds.join(",")})`);
        if (deleteError) {
          console.warn("Delete warning:", deleteError);
        }
      } else {
        // All rows deleted — remove everything for this sheet
        const { error: deleteError } = await supabase.from("ad_sheet_rows").delete().eq("sheet_id", sheetId);
        if (deleteError) {
          console.warn("Delete all warning:", deleteError);
        }
      }

      // Only upsert if there are rows remaining
      if (currentRows.length > 0) {
        const rowsToUpsert = currentRows.map((row, index) => mapAdRowToDbInsert(row, sheetId, user.id, index));
        const { error: upsertError } = await supabase
          .from("ad_sheet_rows")
          .upsert(rowsToUpsert, { onConflict: "id", ignoreDuplicates: false });
        if (upsertError) throw upsertError;
      }

      // Always update the sheet timestamp
      await supabase.from("ad_sheets").update({ updated_at: new Date().toISOString() }).eq("id", sheetId);
    } catch (error) {
      console.error("Error saving rows:", error);
      setSaveError(error instanceof Error ? error.message : "Failed to save");
    } finally {
      setIsSaving(false);
    }
  }, [sheetId, user?.id]);

  const scheduleSave = useCallback(() => {
    pendingSaveRef.current = true;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      if (pendingSaveRef.current) {
        saveToDatabase();
      }
    }, DEBOUNCE_DELAY);
  }, [saveToDatabase]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      // Save pending changes on unmount
      if (pendingSaveRef.current) {
        saveToDatabase();
      }
    };
  }, [saveToDatabase]);

  // ===== UPDATE ROW (Optimistic) =====
  const updateRow = useCallback(
    (rowId: string, field: keyof AdRow | string, value: unknown) => {
      // Eagerly prefetch ad sets when campaign is selected
      if (field === "campaign" && value && typeof value === "object" && (value as any).id) {
        const campaignId = (value as any).id;
        const adAccountId = adAccountIdRef.current;
        if (adAccountId && user?.id) {
          queryClient.fetchQuery({
            queryKey: ["ad-sets", adAccountId, campaignId],
            queryFn: async () => {
              const { data: cachedData } = await supabase
                .from("meta_cached_ad_sets")
                .select("ad_set_id, ad_set_name, display_name, status, optimization_goal, promoted_object, destination_type, billing_event")
                .eq("user_id", user.id)
                .eq("ad_account_id", adAccountId)
                .eq("campaign_id", campaignId)
                .in("status", ["ACTIVE", "PAUSED"])
                .order("ad_set_name");

              if (cachedData && cachedData.length > 0) {
                return {
                  campaignId,
                  adSets: cachedData.map((a) => ({
                    id: a.ad_set_id,
                    name: a.display_name || a.ad_set_name || "",
                    optimizationGoal: a.optimization_goal || null,
                    promotedObject: a.promoted_object as any,
                    destinationType: a.destination_type || null,
                    billingEvent: a.billing_event || null,
                  })),
                };
              }

              const { data: edgeData, error: edgeError } = await supabase.functions.invoke("process-integration", {
                body: {
                  action: "get-adsets",
                  user_id: user.id,
                  campaign_id: campaignId,
                  ad_account_id: adAccountId,
                  filtering: [{ field: "effective_status", operator: "IN", value: ["ACTIVE", "PAUSED"] }],
                },
              });

              if (edgeError) {
                throw edgeError;
              }

              const list = edgeData
                ? (Array.isArray(edgeData) ? edgeData : edgeData?.data || [])
                : [];
              const parsedAdSets = list.map((a: any) => ({
                id: a.adset_id || a.ad_set_id || a.id || "",
                name: a.display_name || a.adset_name || a.ad_set_name || a.name || "",
                optimizationGoal: a.optimization_goal || null,
                promotedObject: a.promoted_object || null,
                destinationType: a.destination_type || null,
                billingEvent: a.billing_event || null,
              })).filter((a: any) => a.id !== "");

              if (parsedAdSets.length > 0) {
                return { campaignId, adSets: parsedAdSets };
              }

              // n8n writes to DB cache during edge function call but may return empty response.
              // Re-query DB to pick up data n8n wrote.
              const { data: refreshedCache } = await supabase
                .from("meta_cached_ad_sets")
                .select("ad_set_id, ad_set_name, display_name, status, optimization_goal, promoted_object, destination_type, billing_event")
                .eq("user_id", user.id)
                .eq("ad_account_id", adAccountId)
                .eq("campaign_id", campaignId)
                .in("status", ["ACTIVE", "PAUSED"])
                .order("ad_set_name");

              if (refreshedCache && refreshedCache.length > 0) {
                return {
                  campaignId,
                  adSets: refreshedCache.map((a) => ({
                    id: a.ad_set_id,
                    name: a.display_name || a.ad_set_name || "",
                    optimizationGoal: a.optimization_goal || null,
                    promotedObject: a.promoted_object as any,
                    destinationType: a.destination_type || null,
                    billingEvent: a.billing_event || null,
                  })),
                };
              }

              return { campaignId, adSets: [] };
            },
            staleTime: CACHE_TTL,
          });
        }
      }

      setRows((prevRows) => {
        const newRows = prevRows.map((row) => {
          if (row.id !== rowId) return row;

          // Handle nested field updates (e.g., "creatives.feed")
          if (field.includes(".")) {
            const [parent, child] = field.split(".");
            const parentValue = row[parent as keyof AdRow];

            if (typeof parentValue === "object" && parentValue !== null) {
              return {
                ...row,
                [parent]: {
                  ...parentValue,
                  [child]: value,
                },
              };
            }
            return row;
          }

          // Handle array field updates (e.g., "primaryTexts[0]")
          const arrayMatch = field.match(/^(\w+)\[(\d+)\]$/);
          if (arrayMatch) {
            const [, arrayField, indexStr] = arrayMatch;
            const index = parseInt(indexStr, 10);
            const arrayValue = row[arrayField as keyof AdRow];

            if (Array.isArray(arrayValue)) {
              const newArray = [...arrayValue] as unknown[];
              newArray[index] = value;
              return {
                ...row,
                [arrayField]: newArray,
              };
            }
            return row;
          }

          // Standard field update
          return {
            ...row,
            [field]: value,
          };
        });

        // Fingerprint check
        const newFingerprint = generateRowsFingerprint(newRows);
        if (newFingerprint === rowsFingerprintRef.current) {
          return prevRows; // No actual change
        }

        rowsFingerprintRef.current = newFingerprint;
        return newRows;
      });

      // Schedule debounced save
      scheduleSave();
    },
    [scheduleSave, rows],
  );

  // Batch update for multiple fields at once (more efficient)
  const updateRowBatch = useCallback(
    (rowId: string, updates: Partial<AdRow>) => {
      setRows((prevRows) => {
        const newRows = prevRows.map((row) => {
          if (row.id !== rowId) return row;
          return { ...row, ...updates };
        });

        const newFingerprint = generateRowsFingerprint(newRows);
        if (newFingerprint === rowsFingerprintRef.current) {
          return prevRows;
        }

        rowsFingerprintRef.current = newFingerprint;
        return newRows;
      });

      scheduleSave();
    },
    [scheduleSave],
  );

  // ===== ADD ROW =====
  const addRow = useCallback(
    (afterRowId?: string) => {
      const adAccount = adAccountIdRef.current
        ? { id: adAccountIdRef.current, name: adAccountIdRef.current }
        : undefined;

      const newRow = createEmptyRow(adAccount);

      // Apply ad_account_settings defaults if available
      const settings = accountSettingsRef.current;
      if (settings) {
        if (settings.default_fb_page_id) {
          newRow.facebookPage = {
            id: settings.default_fb_page_id,
            name: settings.default_fb_page_name || settings.default_fb_page_id,
          };
        }
        if (settings.default_ig_account_id) {
          newRow.instagramAccount = {
            id: settings.default_ig_account_id,
            username: settings.default_ig_account_name || settings.default_ig_account_id,
          };
        }
        if (settings.default_utm_parameters) {
          newRow.utmParameters = settings.default_utm_parameters;
        }
        if (settings.default_ad_status) {
          newRow.status = settings.default_ad_status as "PAUSED" | "ACTIVE";
        }
      }

      setRows((prevRows) => {
        let newRows: AdRow[];

        if (afterRowId) {
          const index = prevRows.findIndex((r) => r.id === afterRowId);
          if (index >= 0) {
            newRows = [...prevRows.slice(0, index + 1), newRow, ...prevRows.slice(index + 1)];
          } else {
            newRows = [...prevRows, newRow];
          }
        } else {
          newRows = [...prevRows, newRow];
        }

        rowsFingerprintRef.current = generateRowsFingerprint(newRows);
        return newRows;
      });

      scheduleSave();
    },
    [scheduleSave],
  );

  // ===== ADD MULTIPLE ROWS (bulk insert with defaults, direct to Supabase) =====
  const addMultipleRows = useCallback(
    async (
      resolvedRows: Array<{
        ad_format: string;
        ad_name: string;
        creative_feed?: string | null;
        meta_creative_id_feed?: string | null;
        creative_vertical?: string | null;
        meta_creative_id_vertical?: string | null;
        creative_horizontal?: string | null;
        meta_creative_id_horizontal?: string | null;
        thumbnail_url?: string | null;
        carousel_data?: unknown;
        flexible_data?: unknown;
        collection_headline_type?: string | null;
        collection_headline_value?: string | null;
        catalog_cta_type?: string | null;
        description_1?: string | null;
      }>,
    ) => {
      if (resolvedRows.length === 0) return;
      if (!user?.id || !sheetId) return;

      const settings = accountSettingsRef.current;
      const currentRowCount = rowsRef.current.length;

      const inserts = resolvedRows.map((resolved, index) => ({
        sheet_id: sheetId,
        user_id: user.id,
        stage: "Draft",
        ad_name: resolved.ad_name || null,
        ad_format: resolved.ad_format || "Single Image or Video",
        square_creative: resolved.creative_feed || null,
        meta_creative_id: resolved.meta_creative_id_feed || null,
        vertical_creative: resolved.creative_vertical || null,
        horizontal_creative: resolved.creative_horizontal || null,
        row_order: (currentRowCount + index) * 10,
        // Account defaults from ref
        facebook_page_id: settings?.default_fb_page_id || null,
        facebook_page_name: settings?.default_fb_page_name || null,
        instagram_actor_id: settings?.default_ig_account_id || null,
        instagram_name: settings?.default_ig_account_name || null,
        utm_parameters: settings?.default_utm_parameters || null,
        status: settings?.default_ad_status || "PAUSED",
        // Format-specific JSONB - cast to any for Supabase Json compatibility
        carousel_data: (resolved.carousel_data ?? null) as any,
        flexible_data: (resolved.flexible_data ?? null) as any,
        // Collection fields
        collection_headline_type: resolved.collection_headline_type || null,
        collection_headline_value: resolved.collection_headline_value || null,
        catalog_cta_type: resolved.catalog_cta_type || null,
        description_1: resolved.description_1 || null,
      }));

      const { error } = await supabase.from("ad_sheet_rows").insert(inserts).select();

      if (error) {
        console.error("[addMultipleRows] Insert failed:", error);
        throw error;
      }

      // Invalidate query to refresh table
      queryClient.invalidateQueries({ queryKey: ["ad-sheet-rows", sheetId] });
    },
    [user?.id, sheetId, queryClient],
  );

  // ===== DELETE ROWS =====
  const deleteRows = useCallback(
    (rowIds: string[]) => {
      if (rowIds.length === 0) return;

      const rowIdSet = new Set(rowIds);

      setRows((prevRows) => {
        const newRows = prevRows.filter((r) => !rowIdSet.has(r.id));

        rowsFingerprintRef.current = generateRowsFingerprint(newRows);
        return newRows;
      });

      scheduleSave();
    },
    [scheduleSave],
  );

  // ===== DUPLICATE ROW =====
  const duplicateRow = useCallback(
    (rowId: string) => {
      setRows((prevRows) => {
        const index = prevRows.findIndex((r) => r.id === rowId);
        if (index < 0) return prevRows;

        const rowToDuplicate = prevRows[index];
        const duplicatedRow: AdRow = {
          ...JSON.parse(JSON.stringify(rowToDuplicate)),
          id: crypto.randomUUID(),
          adName: rowToDuplicate.adName ? `${rowToDuplicate.adName} (copy)` : "",
          stage: "Draft" as AdStage,
          meta_ad_id: null, // New ad, not yet created in Meta
        };

        const newRows = [...prevRows.slice(0, index + 1), duplicatedRow, ...prevRows.slice(index + 1)];

        rowsFingerprintRef.current = generateRowsFingerprint(newRows);
        return newRows;
      });

      scheduleSave();
    },
    [scheduleSave],
  );

  // ===== REORDER ROWS =====
  const reorderRows = useCallback(
    (startIndex: number, endIndex: number) => {
      setRows((prevRows) => {
        const newRows = [...prevRows];
        const [removed] = newRows.splice(startIndex, 1);
        newRows.splice(endIndex, 0, removed);

        rowsFingerprintRef.current = generateRowsFingerprint(newRows);
        return newRows;
      });

      scheduleSave();
    },
    [scheduleSave],
  );

  // Synchronous getter for ad sets — plain function, recomputed every render
  // No useCallback to avoid stale closure issues with availableAdSets
  const getAdSetsForCampaign = (campaignId: string): AdSet[] => {
    const cleanCampaignId = campaignId.replace(/[^a-zA-Z0-9_]+$/g, "").trim();

    // 1. availableAdSets (populated by useQueries — recomputed every render)
    if (availableAdSets[cleanCampaignId]?.length > 0) {
      return availableAdSets[cleanCampaignId];
    }

    // 2. React Query cache fallback
    const cached = queryClient.getQueryData<{ campaignId: string; adSets: AdSet[] }>([
      "ad-sets",
      adAccountIdRef.current,
      cleanCampaignId,
    ]);
    if (cached?.adSets?.length) {
      return cached.adSets;
    }

    return [];
  };

  // Get full ad set details including optimization_goal and promoted_object
  const getAdSetWithDetails = (campaignId: string, adSetId: string): AdSet | null => {
    const adSets = getAdSetsForCampaign(campaignId);
    return adSets.find((as) => as.id === adSetId) || null;
  };

  // ===== REFRESH META SYNC =====
  const refreshMetaSync = useCallback(async () => {
    if (!user?.id || !adAccountIdRef.current) return;

    try {
      const { error: proxyError } = await supabase.functions.invoke("n8n-proxy", {
        body: {
          action: "SYNC_META",
          payload: {
            ad_account_id: adAccountIdRef.current,
          },
        },
      });

      if (proxyError) {
        throw new Error("Meta sync failed");
      }

      // Invalidate React Query caches to refetch fresh data
      queryClient.invalidateQueries({ queryKey: ["cached-campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["ad-sets", adAccountIdRef.current] });
    } catch (error) {
      console.error("Error refreshing Meta sync:", error);
      throw error;
    }
  }, [user?.id, queryClient]);

  // ===== FORCE SAVE (immediate, no debounce) =====
  const forceSave = useCallback(async () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    pendingSaveRef.current = false;
    await saveToDatabase();
  }, [saveToDatabase]);

  // ===== TOGGLE OPTIONAL COLUMN =====
  const toggleOptionalColumn = useCallback(
    async (column: OptionalColumn) => {
      setVisibleOptionalColumns((prev) => {
        const newColumns = prev.includes(column) ? prev.filter((c) => c !== column) : [...prev, column];

        // Persist to database (fire-and-forget)
        if (sheetId) {
          supabase
            .from("ad_sheets")
            .update({ visible_columns: newColumns })
            .eq("id", sheetId)
            .then(({ error }) => {
              if (error) console.error("Failed to persist column visibility:", error);
            });
        }

        return newColumns;
      });
    },
    [sheetId],
  );

  // ===== COMPUTED VALUES =====
  const isLoading = isLoadingSheet || isLoadingRows;

  const sheetInfo = useMemo(() => {
    if (!sheetData) return null;
    return {
      adAccountId: sheetData.ad_account_id,
      name: sheetData.name,
    };
  }, [sheetData]);

  // ===== LAUNCH ADS FUNCTION =====
  const launchAds = useCallback(
    async (rowIds: string[]): Promise<LaunchResult> => {
      console.log("[launchAds] Function called with rowIds:", rowIds);
      if (!user?.id || !sheetId) {
        return {
          success: false,
          launched: 0,
          failed: 0,
          errors: [{ rowId: "", adName: "", error: "Not authenticated" }],
        };
      }

      const rowsToLaunch = rowsRef.current.filter((r) => rowIds.includes(r.id) && r.stage === "Draft");

      if (rowsToLaunch.length === 0) {
        return {
          success: false,
          launched: 0,
          failed: rowIds.length,
          errors: [{ rowId: "", adName: "", error: "No valid draft rows to launch" }],
        };
      }

      // ===== PRE-LAUNCH VALIDATION =====
      const campaigns = dropdownOptionsRef.current.campaigns;
      const adSetsLookup = dropdownOptionsRef.current.adSets;
      const blockErrors: string[] = [];
      const warnMessages: string[] = [];

      for (const row of rowsToLaunch) {
        const adLabel = row.adName || row.id.slice(0, 8);
        const isCarousel = row.adFormat === "Carousel";

        // === Carousel-specific validation ===
        if (isCarousel) {
          if (!row.carouselData?.cards?.length || row.carouselData.cards.length < 2) {
            blockErrors.push(`Row "${adLabel}": Minimum 2 carousel cards required`);
          }
          if (!row.primaryTexts?.[0]?.trim()) {
            blockErrors.push(`Row "${adLabel}": Primary text is required`);
          }
          if (!row.url?.trim()) {
            blockErrors.push(`Row "${adLabel}": Destination URL is required`);
          }
          if (!row.campaign || !row.adSet) {
            blockErrors.push(`Row "${adLabel}": Campaign and Ad Set are required`);
          }
          if (!row.facebookPage?.id) {
            blockErrors.push(`Row "${adLabel}": Facebook Page is required`);
          }
          if (!row.instagramAccount?.id) {
            blockErrors.push(`Row "${adLabel}": Instagram Account is required`);
          }
          // Skip normal creative/headline validation for carousel
          continue;
        }

        // === Collection-specific validation ===
        const isCollection = row.adFormat === "Collection";
        if (isCollection) {
          if (!row.catalogId) {
            blockErrors.push(`Row "${adLabel}": Catalog is required for Collection ads`);
          }
          if (!row.productSetId) {
            blockErrors.push(`Row "${adLabel}": Product Set is required for Collection ads`);
          }
          if (!row.campaign || !row.adSet) {
            blockErrors.push(`Row "${adLabel}": Campaign and Ad Set are required`);
          }
          if (!row.facebookPage?.id) {
            blockErrors.push(`Row "${adLabel}": Facebook Page is required`);
          }
          if (!row.instagramAccount?.id) {
            blockErrors.push(`Row "${adLabel}": Instagram Account is required`);
          }
          // Skip normal creative/headline validation for collection
          continue;
        }

        // === Flexible-specific validation ===
        const isFlexible = row.adFormat === "Flexible";
        if (isFlexible) {
          if (!row.flexibleData?.assets?.length || row.flexibleData.assets.length < 2) {
            blockErrors.push(`Row "${adLabel}": Minimum 2 flexible assets required`);
          }
          if (!row.campaign || !row.adSet) {
            blockErrors.push(`Row "${adLabel}": Campaign and Ad Set are required`);
          }
          if (!row.facebookPage?.id) {
            blockErrors.push(`Row "${adLabel}": Facebook Page is required`);
          }
          if (!row.instagramAccount?.id) {
            blockErrors.push(`Row "${adLabel}": Instagram Account is required`);
          }
          // Skip normal creative validation for flexible
          continue;
        }

        // === Catalog-specific validation ===
        if (row.adFormat === "Catalog") {
          if (!row.catalogId && !catalogId) {
            blockErrors.push(`Row "${adLabel}": Catalog format requires a catalog. Select one in the sheet header.`);
          }
          if (!row.productSetId) {
            blockErrors.push(`Row "${adLabel}": Catalog format requires a product set.`);
          }
          if (!row.facebookPage?.id) {
            blockErrors.push(`Row "${adLabel}": Facebook Page is required for Catalog ads`);
          }
          // Skip normal creative validation for catalog
          continue;
        }

        // Look up campaign objective
        const campaignId = row.campaign?.id;
        const campaign = campaignId ? campaigns.find((c) => c.id === campaignId) : null;
        const objective = campaign?.objective || "";

        // Look up ad set destination_type
        const adSetId = row.adSet?.id;
        let destType = "";
        if (campaignId && adSetId) {
          const cleanCampaignId = campaignId.replace(/[^a-zA-Z0-9_]+$/g, "").trim();
          const adSets = adSetsLookup[cleanCampaignId] || [];
          const adSet = adSets.find((a) => a.id === adSetId);
          const rawDest = adSet?.destinationType || "";
          destType = rawDest === "UNDEFINED" ? "" : rawDest;
        }

        const url = (row.url || "").trim();

        const isUrlRequiredDest = URL_REQUIRED_DESTINATIONS.includes(destType as any);

        // Destination URL not required for awareness/engagement ON_AD
        const isAwarenessOrEngagement = ["OUTCOME_AWARENESS", "OUTCOME_ENGAGEMENT"].includes(objective);
        const isOnAd = destType === "ON_AD";
        const urlExempt = isAwarenessOrEngagement && isOnAd;

        // BLOCK rules
        if (objective === "OUTCOME_TRAFFIC" && isUrlRequiredDest && !url) {
          blockErrors.push(`Row "${adLabel}": URL is required for Traffic campaigns targeting ${destType}`);
        }
        if (objective === "OUTCOME_ENGAGEMENT" && isUrlRequiredDest && !urlExempt && !url) {
          blockErrors.push(`Row "${adLabel}": URL is required for Engagement campaigns targeting ${destType}`);
        }
        if (objective === "OUTCOME_LEADS" && !isUrlRequiredDest && !row.leadgenFormId) {
          blockErrors.push(`Row "${adLabel}": Leadgen Form is required for Lead campaigns using Instant Forms`);
        }
        if (objective === "OUTCOME_SALES" && isUrlRequiredDest && !url) {
          blockErrors.push(`Row "${adLabel}": URL is required for Sales campaigns targeting ${destType}`);
        }

        // WARN rules
        if (objective === "OUTCOME_AWARENESS" && url) {
          warnMessages.push(`Row "${adLabel}": Awareness ads don't use a URL — it will be ignored`);
        }
      }

      // Show warnings (non-blocking)
      if (warnMessages.length > 0) {
        const { toast } = await import("@/hooks/use-toast");
        toast({
          title: `${warnMessages.length} warning${warnMessages.length > 1 ? "s" : ""}`,
          description: warnMessages.join("\n"),
          variant: "default",
        });
      }

      // Block on errors
      if (blockErrors.length > 0) {
        const { toast } = await import("@/hooks/use-toast");
        toast({
          title: `${blockErrors.length} validation error${blockErrors.length > 1 ? "s" : ""} — launch blocked`,
          description: blockErrors.join("\n"),
          variant: "destructive",
        });
        return {
          success: false,
          launched: 0,
          failed: blockErrors.length,
          errors: blockErrors.map((e) => ({ rowId: "", adName: "", error: e })),
        };
      }

      // Safety: Save current state before launching
      const result: LaunchResult = { success: true, launched: 0, failed: 0, errors: [] };
      setLaunchProgress({ current: 0, total: rowsToLaunch.length });
      setProcessingRowIds(new Set(rowsToLaunch.map((r) => r.id)));
      await new Promise((resolve) => setTimeout(resolve, 50));
      await forceSave();

      for (let i = 0; i < rowsToLaunch.length; i++) {
        const row = rowsToLaunch[i];
        setLaunchProgress({ current: i + 1, total: rowsToLaunch.length });
        await new Promise((resolve) => setTimeout(resolve, 0));

        try {
          // Generate unique job ID for this ad launch
          const jobId = crypto.randomUUID();

          // Build account-level enhancements fallback (only when no template)
          let accountEnhancements = null;
          if (!row.ad_template_id) {
            const settings = accountSettingsRef.current;
            if (settings) {
              const format = row.adFormat;
              let enhancements: any = null;

              if (format === "Carousel") {
                enhancements = (settings as any).carousel_enhancements || null;
              } else if (format === "Collection") {
                enhancements = (settings as any).collection_enhancements || null;
              } else if (format === "Catalog") {
                enhancements = settings.enhancements_catalog || null;
              } else if (format === "Flexible") {
                enhancements = {
                  flexible_advantage_plus_creative: (settings as any).flexible_advantage_plus_creative ?? false,
                };
              } else {
                // Single Image or Video — detect by filename
                const feedFilename = row.creatives?.feed?.filename || "";
                const isVideo = feedFilename.match(/\.(mp4|mov|avi|wmv|webm)$/i) !== null;
                enhancements = isVideo ? settings.video_enhancements : settings.image_enhancements;
              }

              if (enhancements) {
                accountEnhancements = {
                  source: "account_settings",
                  enhancements,
                };
              }
            }
          }

          // ── CAROUSEL: transform cards with n8n-compatible field names ──
          const isCarouselRow = row.adFormat === "Carousel";
          const carouselPayload =
            isCarouselRow && row.carouselData?.cards?.length
              ? {
                  cards: row.carouselData.cards.map((card) => ({
                    // n8n Master Router expects creative_name (not creative_filename)
                    creative_name: card.creative_filename,
                    creative_name_vertical: card.creative_filename_vertical || null,
                    meta_creative_id: card.meta_creative_id,
                    meta_creative_id_vertical: card.meta_creative_id_vertical || null,
                    headline: card.headline,
                    description: card.description || "",
                    // n8n expects "link" (not "card_url"); fall back to row URL
                    link: card.card_url || row.url || "",
                    cta_type: card.cta_type || row.ctaType || "SHOP_NOW",
                    creative_type: card.creative_type || "image",
                  })),
                }
              : null;

          const { data: launchData, error: launchError } = await supabase.functions.invoke("process-integration", {
            body: {
              action: "launch_ad",
              job_id: jobId,
              user_id: user.id,
              ad_account_id: row.adAccount?.id || adAccountIdRef.current,
              ad_data: (() => {
                // Look up campaign objective
                const rowCampaignId = row.campaign?.id;
                const rowCampaign = rowCampaignId ? campaigns.find((c) => c.id === rowCampaignId) : null;
                const rowObjective = rowCampaign?.objective || "";

                // Look up ad set destination_type
                const rowAdSetId = row.adSet?.id;
                let rowDestType = "";
                if (rowCampaignId && rowAdSetId) {
                  const cleanCId = rowCampaignId.replace(/[^a-zA-Z0-9_]+$/g, "").trim();
                  const rowAdSets = adSetsLookup[cleanCId] || [];
                  const rowAdSet = rowAdSets.find((a) => a.id === rowAdSetId);
                  const rawDest = rowAdSet?.destinationType || "";
                  rowDestType = rawDest === "UNDEFINED" ? "" : rawDest;
                }

                // Look up facebook page name from cached pages
                const facebookPages = dropdownOptionsRef.current.facebookPages;
                const pageName = row.facebookPage?.id
                  ? facebookPages.find((p) => p.id === row.facebookPage?.id)?.name || row.facebookPage?.name || ""
                  : "";

                // ON_PAGE (Page Likes) overrides
                const isOnPage = rowDestType === "ON_PAGE";

                return {
                ad_name: row.adName,
                ad_format: row.adFormat ? AD_FORMAT_MAP[row.adFormat] || row.adFormat.toLowerCase() : "single",
                campaign_id: row.campaign?.id,
                adset_id: row.adSet?.id,
                facebook_page_id: row.facebookPage?.id,
                facebook_page_name: pageName,
                instagram_actor_id: isOnPage ? "" : (row.instagramAccount?.id || ""),
                // Campaign context for n8n Build Creative Spec
                campaign_objective: rowObjective,
                destination_type: rowDestType,
                // Partner accounts
                partnership_page_id: row.partnerFacebookPage?.id || "",
                partnership_instagram_user_id: row.partnerInstagramAccount?.id || "",
                threads_page_id: row.threadsPageId || "",
                // Send filenames for n8n compatibility (not Meta creative IDs)
                creative_square_name:
                  row.adFormat === "Collection"
                    ? (row.collectionData as any)?.creativeName || row.creatives?.feed?.filename || ""
                    : row.creatives?.feed?.filename || "",
                creative_vertical_name: row.creatives?.vertical?.filename || "",
                primary_texts:
                  row.adFormat === "Collection"
                    ? [row.primaryTexts[0]].filter((t) => t.trim())
                    : row.primaryTexts.filter((t) => t.trim()),
                headlines: isOnPage ? [] : (
                  row.adFormat === "Collection"
                    ? [row.headlines[0]].filter((h) => h.trim())
                    : row.headlines.filter((h) => h.trim())
                ),
                descriptions: row.adFormat === "Collection" ? [] : row.descriptions?.filter((d) => d.trim()) || [],
                destination_url: isOnPage ? "" : (row.url || ""),
                display_link: isOnPage ? "" : (row.adFormat === "Collection" ? "" : row.displayLink || ""),
                utm_parameters: isOnPage ? "" : (row.utmParameters || ""),
                cta_type: isOnPage ? "" : row.ctaType,
                status: row.status,
                // Ad schedule (ISO 8601 strings)
                ad_schedule_start: row.ad_schedule_start || null,
                ad_schedule_end: row.ad_schedule_end || null,
                // Template ID for n8n to fetch enhancement settings
                ad_template_id: row.ad_template_id || null,
                // Account-level enhancements fallback (when no template)
                account_enhancements: accountEnhancements,
                // Multi-advertiser opt-in from account settings
                multi_advertiser_opt_in: accountSettingsRef.current?.multi_advertiser_opt_in ?? false,
                // Catalog and Product Set fields
                catalog_id: isOnPage ? null : (row.catalogId || null),
                catalog_name: isOnPage ? null : (row.catalogName || null),
                product_set_id: isOnPage ? null : (row.productSetId || null),
                product_set_name: isOnPage ? null : (row.productSetName || null),
                // ── CATALOG-specific fields ──
                headline_type: row.catalogAdData?.headlineType || null,
                headline_value: row.catalogAdData?.headlineValue || null,
                description_type: row.catalogAdData?.descriptionType || null,
                description_value: row.catalogAdData?.descriptionValue || null,
                catalog_format_type: row.catalogAdData?.formatType || "carousel",
                automated_product_tags: row.catalogAdData?.automatedProductTags ?? false,
                // ── COLLECTION-specific fields ──
                button_label: row.adFormat === "Collection" ? row.collectionData?.buttonLabel || "Shop Now" : undefined,
                item_headline:
                  row.adFormat === "Collection" ? row.collectionData?.itemHeadline || "{{product.name}}" : undefined,
                item_description:
                  row.adFormat === "Collection"
                    ? row.collectionData?.itemDescription || "{{product.current_price}}"
                    : undefined,
                collection_headline_type:
                  row.adFormat === "Collection" ? row.collectionData?.headlineType || "macro" : undefined,
                collection_headline_value:
                  row.adFormat === "Collection" ? row.collectionData?.headlineValue || "{{product.name}}" : undefined,
                // Lead gen form (for LEAD_GENERATION optimization goal)
                leadgen_form_id: row.leadgenFormId || null,
                // Product tags (up to 5 Meta product IDs) — not for Collection
                product_tags:
                  row.adFormat === "Collection" ? [] : row.productTags?.filter((id) => id && id.trim() !== "") || [],
                // ── CAROUSEL: transformed cards with n8n field names ──
                carousel_data: carouselPayload,
                // ── FLEXIBLE: asset list for flexible ads ──
                flexible_data:
                  row.adFormat === "Flexible" && row.flexibleData
                    ? {
                        assets: row.flexibleData.assets.map((a) => ({
                          type: a.type,
                          meta_creative_id: a.meta_creative_id,
                          name: a.filename,
                          thumbnail_url: a.thumbnail_url,
                        })),
                        assetType: row.flexibleData.assetType,
                      }
                    : null,
                // Tracking pixel for non-Sales campaigns (awareness, engagement)
                tracking_pixel_id:
                  accountSettingsRef.current?.website_events_enabled && accountSettingsRef.current?.default_pixel_id
                    ? accountSettingsRef.current.default_pixel_id
                    : null,
                };
              })(),
            },
          });

          if (launchError) throw new Error(launchError.message);

          // Extract IDs from launch response
          const metaAdId = launchData?.meta_ad_id || launchData?.ad_id || null;
          const metaCreativeId = launchData?.meta_creative_id || launchData?.creative_id || null;

          // Update row locally to Launched
          setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, stage: "Launched" as const } : r)));

          // Persist launch state to Supabase
          await supabase
            .from("ad_sheet_rows")
            .update({
              stage: "Launched",
              meta_ad_id: metaAdId,
              meta_creative_id: metaCreativeId,
              launched_at: new Date().toISOString(),
              launch_error: null,
            })
            .eq("id", row.id);

          result.launched++;
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : "Unknown error";
          result.failed++;
          result.errors.push({ rowId: row.id, adName: row.adName, error: errorMessage });

          // Persist error state to Supabase
          await supabase
            .from("ad_sheet_rows")
            .update({
              stage: "Draft",
              launch_error: errorMessage,
            })
            .eq("id", row.id);
        }

        // Remove from processing set
        setProcessingRowIds((prev) => {
          const next = new Set(prev);
          next.delete(row.id);
          return next;
        });

        // Rate limiting delay
        if (i < rowsToLaunch.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      }

      setLaunchProgress(null);
      setProcessingRowIds(new Set());
      queryClient.invalidateQueries({ queryKey: ["ad-sheet-rows", sheetId] });

      result.success = result.failed === 0;
      return result;
    },
    [user?.id, sheetId, forceSave, queryClient],
  );

  // ===== ROW SELECTION =====
  const toggleRowSelection = useCallback((rowId: string) => {
    setSelectedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  }, []);

  const selectAllRows = useCallback(() => {
    setSelectedRowIds(new Set(rows.map((row) => row.id)));
  }, [rows]);

  const clearSelection = useCallback(() => {
    setSelectedRowIds(new Set());
  }, []);

  const allRowsSelected = rows.length > 0 && selectedRowIds.size === rows.length;
  const someRowsSelected = selectedRowIds.size > 0 && selectedRowIds.size < rows.length;

  // ===== SET CATALOG ID (persists to database) =====
  const setCatalogId = useCallback(
    async (newCatalogId: string | null) => {
      setCatalogIdState(newCatalogId);

      // Persist to database
      if (sheetId) {
        const { error } = await supabase
          .from("ad_sheets")
          .update({ catalog_id: newCatalogId } as any)
          .eq("id", sheetId);

        if (error) console.error("Failed to persist catalog_id:", error);
      }
    },
    [sheetId],
  );

  // ===== NAMING CONVENTION =====
  const namingConvention = useMemo(() => {
    const nc = accountSettings?.naming_convention as GenerateAdNameParams["namingConvention"];
    return nc || null;
  }, [accountSettings?.naming_convention]);

  const generateRowName = useCallback(
    (row: AdRow): string => {
      return generateAdNameUtil({
        namingConvention,
        adFormat: row.adFormat || undefined,
        creativeFeedFilename:
          row.adFormat === "Collection"
            ? row.collectionData?.creativeName || row.creatives?.feed?.filename
            : row.creatives?.feed?.filename,
        creativeVerticalFilename: row.creatives?.vertical?.filename,
        creativeHorizontalFilename: row.creatives?.horizontal?.filename,
        groupType: row.creatives?.feed?.group_type || undefined,
        creativeType: undefined,
        campaignName: row.campaign?.name,
        adsetName: row.adSet?.name,
        creativeTags: row.creativeTags || undefined,
      });
    },
    [namingConvention],
  );

  return {
    rows,
    isLoading,
    isSaving,
    saveError,
    updateRow,
    updateRowBatch,
    addRow,
    addMultipleRows,
    deleteRows,
    duplicateRow,
    reorderRows,
    dropdownOptions,
    getAdSetsForCampaign,
    getAdSetWithDetails,
    refreshMetaSync,
    forceSave,
    sheetInfo,
    processingRowIds,
    launchProgress,
    launchAds,
    visibleOptionalColumns,
    toggleOptionalColumn,
    selectedRowIds,
    toggleRowSelection,
    selectAllRows,
    clearSelection,
    allRowsSelected,
    someRowsSelected,
    catalogId,
    setCatalogId,
    namingConvention,
    generateRowName,
  };
};
